// System
import { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { LanguageTextInput } from './LanguageTextInput';

// Components
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { TableColumnHeaderLayout } from '@Components/shared/tables/columns';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { LocalizationItemDto } from '@contracts/api/dto/localization/LocalizationDto';

// Utils
import {
  localizationListColumns,
  LocalizationListFilter,
  LocalizationListColumns,
} from '@utils/tables/localizations/localizationsListFilter';
import { localizationsMap } from '../LocalizationsTitle/LocalizationsListArray';
import { useLocalization } from '@Modal/hooks/localization/useLocalization';

type HeaderOptions = {
  filter: Filterable<LocalizationListFilter>;
};

type Columns = TableColumnDescriptionArray<
  LocalizationItemDto,
  HeaderOptions,
  typeof localizationListColumns
>;

type Column = TableColumnDescription<LocalizationItemDto, HeaderOptions, LocalizationListColumns>;

export const useLocalizationTableColumns = () => {
  const ellipsis = useEllipsis();
  const { handleUpdateLocalization } = useLocalization();
  return useMemo(
    (): Column[] =>
      [
        {
          name: 'localizations.key',
          title: 'Key',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <Tooltip sx={{ ...ellipsis }} title={row?.key || 'Not fetched'} arrow placement="top">
              <Box sx={{ ...ellipsis }}>{row?.key || 'Not Fetched'}</Box>
            </Tooltip>
          ),
        },
        {
          name: 'localizations.ka',
          title: `${localizationsMap.ka}`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ka || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ka',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ge',
          title: `${localizationsMap.ge}`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ge || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ge',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ru',
          title: `${localizationsMap.ru} (ru)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ru || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ru',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.en',
          title: `${localizationsMap.en} (en)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.en || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'en',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.zh',
          title: `${localizationsMap.zh} (zh)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.zh || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'zh',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.da',
          title: `${localizationsMap.da} (da)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.da || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'da',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.fa',
          title: `${localizationsMap.fa} (fa)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.fa || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'fa',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ua',
          title: `${localizationsMap.ua} (ua)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ua || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ua',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.tr',
          title: `${localizationsMap.tr} (tr)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.tr || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'tr',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.de',
          title: `${localizationsMap.de} (de)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.de || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'de',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.hy',
          title: `${localizationsMap.hy} (hy)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.hy || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'hy',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.sv',
          title: `${localizationsMap.sv} (sv)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.sv || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'sv',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ar',
          title: `${localizationsMap.ar} (ar)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ar || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ar',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.es',
          title: `${localizationsMap.es} (es)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.es || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'es',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.fr',
          title: `${localizationsMap.fr} (fr)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.fr || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'fr',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ro',
          title: `${localizationsMap.ro} (ro)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ro || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ro',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.el',
          title: `${localizationsMap.el} (el)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.el || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'el',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.hi',
          title: `${localizationsMap.hi} (hi)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.hi || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'hi',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.pt',
          title: `${localizationsMap.pt} (pt)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.pt || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'pt',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.br',
          title: `${localizationsMap.br} (br)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.br || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'br',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.sw',
          title: `${localizationsMap.sw} (sw)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.sw || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'sw',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.qwerty',
          title: `${localizationsMap.qwerty}`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.qwerty || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'qwerty',
                })
              }
            />
          ),
        },
        //
        {
          name: 'localizations.th',
          title: `${localizationsMap.th} (th)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.th || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'th',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.id',
          title: `${localizationsMap.id} (id)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.id || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'id',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.vi',
          title: `${localizationsMap.vi} (vi)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.vi || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'vi',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ko',
          title: `${localizationsMap.ko} (ko)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ko || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ko',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.hr',
          title: `${localizationsMap.hr} (hr)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.hr || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'hr',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.is',
          title: `${localizationsMap.is} (is)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.is || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'is',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.it',
          title: `${localizationsMap.it} (it)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.it || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'it',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.ja',
          title: `${localizationsMap.ja} (ja)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.ja || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'ja',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.lt',
          title: `${localizationsMap.lt} (lt)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.lt || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'lt',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.hu',
          title: `${localizationsMap.hu} (hu)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.hu || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'hu',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.pl',
          title: `${localizationsMap.pl} (pl)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.pl || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'pl',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.sl',
          title: `${localizationsMap.sl} (sl)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.sl || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'sl',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.nl',
          title: `${localizationsMap.nl} (nl)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.nl || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'nl',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.et',
          title: `${localizationsMap.et} (et)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.et || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'et',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.fi',
          title: `${localizationsMap.fi} (fi)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.fi || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'fi',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.no',
          title: `${localizationsMap.no} (no)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.no || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'no',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.sk',
          title: `${localizationsMap.sk} (sk)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.sk || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'sk',
                })
              }
            />
          ),
        },
        {
          name: 'localizations.zs',
          title: `${localizationsMap.zs} (zs)`,
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeaderLayout title={title} />,
          body: ({ row }) => (
            <LanguageTextInput
              initialValue={row?.zs || null}
              onSave={(value) =>
                handleUpdateLocalization({
                  localizationKey: row?.key,
                  localizationValue: value,
                  language: 'zs',
                })
              }
            />
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
